import { useState } from 'react';
import { Modal } from '../common/Modal';
import { X } from 'lucide-react';
import type { CertificateUploadResult } from '../../hooks/useCertificates';
import type { Holder } from '../../utils/holders';
import { CertificateHolderSelect } from './CertificateHolderSelect';
import { SeletorDeArquivo } from '../common/SeletorDeArquivo';
import {
  avisoDoArquivo,
  enviarEEsquecerSenha,
  formularioVazio,
  podeEnviar,
  type FormularioCertificado,
} from '../../utils/certificateForm';

interface CertificateUploadModalProps {
  onClose: () => void;
  /** Envia o formulário pronto (file, password, label). */
  onUpload: (dados: FormData) => Promise<CertificateUploadResult>;
  /** Chamado depois de cadastrar, com o resultado (o aviso de vencimento vem em `warning`). */
  onUploaded: (resultado: CertificateUploadResult) => void;
  /** D23: titulares ativos para o seletor "Titular (opcional)". */
  holders: Holder[];
  /** Titular já escolhido (cadastro feito de dentro de uma pasta aberta); vazio = "Sem titular". */
  defaultHolderId?: string;
}

const inputStyle: React.CSSProperties = {
  background: 'var(--color-bg-root)',
  border: '1px solid var(--color-border-default)',
  padding: 'var(--space-3)',
  borderRadius: 'var(--radius-md)',
  color: 'var(--color-text-primary)',
};

const labelStyle: React.CSSProperties = { fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)' };

/**
 * Cadastro do certificado A1. O estado do formulário (inclusive a senha) vive só aqui:
 * a senha é apagada depois de cada envio (com ou sem erro) e some junto quando o modal fecha.
 */
export function CertificateUploadModal({ onClose, onUpload, onUploaded, holders, defaultHolderId = '' }: CertificateUploadModalProps) {
  const [form, setForm] = useState<FormularioCertificado>(() => ({ ...formularioVazio(), holderId: defaultHolderId }));
  const [error, setError] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);

  const avisoArquivo = avisoDoArquivo(form.file);

  const fechar = () => {
    setForm(formularioVazio());
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!podeEnviar(form) || isBusy) return;
    setIsBusy(true);
    setError(null);
    const resultado = await enviarEEsquecerSenha(form, onUpload);
    setForm(resultado.formulario);
    setIsBusy(false);
    if (resultado.resposta) {
      onUploaded(resultado.resposta);
      fechar();
    } else {
      setError(resultado.erro);
    }
  };

  return (
    <Modal pedirParaFechar={onClose} protegerDigitacao>
      <div style={{ background: 'var(--color-bg-surface)', border: '1px solid var(--color-border-default)', borderRadius: 'var(--radius-xl)', width: '100%', maxWidth: '520px', padding: 'var(--space-8)', maxHeight: '90vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-4)' }}>
          <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-semibold)', minWidth: 0, overflowWrap: 'anywhere' }}>Cadastrar certificado</h2>
          <button type="button" onClick={fechar} style={{ background: 'transparent', border: 'none', color: 'var(--color-text-secondary)', cursor: 'pointer', flexShrink: 0 }}>
            <X size={24} />
          </button>
        </div>

        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', lineHeight: 1.5, marginBottom: 'var(--space-4)' }}>
          O arquivo do certificado e a senha dele são guardados cifrados (trancados) no Lictor.
          Cada vez que o certificado for usado, o uso fica registrado.
        </p>

        {error && (
          <div style={{ padding: 'var(--space-3)', background: 'var(--color-error-muted)', color: 'var(--color-error)', borderRadius: 'var(--radius-md)', marginBottom: 'var(--space-4)', fontSize: 'var(--text-sm)' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} autoComplete="off" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <label htmlFor="cert-file" style={labelStyle}>Arquivo do certificado (.pfx ou .p12)</label>
            <SeletorDeArquivo
              id="cert-file"
              accept=".pfx,.p12"
              dica=".pfx ou .p12"
              arquivo={form.file}
              onChange={(file) => setForm((f) => ({ ...f, file }))}
            />
            {avisoArquivo && (
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-error)' }}>{avisoArquivo}</span>
            )}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <label htmlFor="cert-password" style={labelStyle}>Senha do certificado</label>
            <input
              id="cert-password"
              type="password"
              autoComplete="new-password"
              value={form.password}
              onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))}
              style={inputStyle}
            />
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            <label htmlFor="cert-label" style={labelStyle}>Nome para identificar (opcional)</label>
            <input
              id="cert-label"
              value={form.label}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              style={inputStyle}
            />
          </div>

          <CertificateHolderSelect
            id="cert-holder"
            value={form.holderId}
            onChange={(holderId) => setForm((f) => ({ ...f, holderId }))}
            holders={holders}
          />

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-4)', marginTop: 'var(--space-2)' }}>
            <button type="button" onClick={fechar} style={{ flex: '1 1 120px', padding: 'var(--space-3)', background: 'transparent', border: '1px solid var(--color-border-default)', color: 'var(--color-text-secondary)', borderRadius: 'var(--radius-md)', cursor: 'pointer' }}>
              Cancelar
            </button>
            <button
              type="submit"
              disabled={!podeEnviar(form) || isBusy}
              style={{ flex: '1 1 120px', padding: 'var(--space-3)', background: 'var(--color-primary-600)', color: 'var(--color-primary-50)', border: 'none', borderRadius: 'var(--radius-md)', cursor: !podeEnviar(form) || isBusy ? 'not-allowed' : 'pointer', opacity: !podeEnviar(form) || isBusy ? 0.6 : 1 }}
            >
              {isBusy ? 'Cadastrando...' : 'Cadastrar'}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}
